import { useMutation, useQueryClient } from '@tanstack/react-query';
import { format, parseISO } from 'date-fns';
import api from '../../utils/api';
import { X, Mail } from 'lucide-react';
import toast from 'react-hot-toast';

export default function MandateScheduleModal({ event, onClose }) {
  const queryClient = useQueryClient();

  const sendMutation = useMutation({
    mutationFn: () =>
      api.post(`/mandates/notifications/${event.notification_id}/send`, {
        recipient_email: event.recipient_email,
        message: event.title,
      }),
    onSuccess: () => {
      toast.success('Marked as sent');
      queryClient.invalidateQueries(['schedules']);
      queryClient.invalidateQueries(['mandate-schedule']);
      onClose();
    },
    onError: (e) => toast.error(e.response?.data?.error || 'Failed'),
  });

  const eventDate = event.start_time ? format(parseISO(event.start_time), 'd MMM yyyy') : '—';
  const dueDate = event.inspection_due_date
    ? format(parseISO(event.inspection_due_date), 'd MMM yyyy')
    : '—';
  const docDeadline = event.document_deadline
    ? format(parseISO(event.document_deadline), 'd MMM yyyy')
    : null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
      <div className="bg-card rounded-lg shadow-xl max-w-md w-full p-6">
        <div className="flex justify-between items-start mb-4">
          <h3 className="text-lg font-semibold pr-4">{event.title}</h3>
          <button type="button" onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X className="w-5 h-5" />
          </button>
        </div>

        <dl className="space-y-2 text-sm">
          <div className="flex justify-between">
            <dt className="text-gray-500">Schedule date</dt>
            <dd className="font-medium">{eventDate}</dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-gray-500">Inspection due</dt>
            <dd className="font-medium">{dueDate}</dd>
          </div>
          {docDeadline && (
            <div className="flex justify-between">
              <dt className="text-gray-500">Documents due</dt>
              <dd className="font-medium text-amber-700">{docDeadline}</dd>
            </div>
          )}
          {event.building_name && (
            <div className="flex justify-between">
              <dt className="text-gray-500">Building</dt>
              <dd>{event.building_name}</dd>
            </div>
          )}
          {event.ss_code && (
            <div className="flex justify-between">
              <dt className="text-gray-500">SS</dt>
              <dd>{event.ss_code}</dd>
            </div>
          )}
          {event.recipient_email && (
            <div className="flex justify-between">
              <dt className="text-gray-500">Contact</dt>
              <dd className="truncate max-w-[12rem]">{event.recipient_email}</dd>
            </div>
          )}
          <div className="flex justify-between">
            <dt className="text-gray-500">Status</dt>
            <dd>{event.status || 'UPCOMING'}</dd>
          </div>
        </dl>

        {event.status !== 'SENT' && event.notification_id && (
          <button
            type="button"
            onClick={() => sendMutation.mutate()}
            disabled={sendMutation.isPending}
            className="mt-6 w-full flex items-center justify-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
          >
            <Mail className="w-4 h-4" />
            Mark notification sent
          </button>
        )}
      </div>
    </div>
  );
}
